import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, KpiCard, useToast,
} from '@kss/ui';
import { useAuth, useHasRole } from '@kss/auth';
import {
  Boxes, Users, ShoppingCart, Receipt, Warehouse as WarehouseIcon,
  Layers, AlertTriangle, IndianRupee, TrendingUp, RefreshCw,
} from 'lucide-react';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  productService, employeeService, salesOrderService, invoiceService,
  stockService, categoryService, storeService, warehouseService,
} from '@kss/api';
import type { Product, Employee, SalesOrder, Invoice, Stock, Category, Store, Warehouse } from '@kss/api';
import { useThemeChartColors } from './shared/dashboardHelpers';
import { RecentNotificationsCard } from '../notifications';

const inr = (n: number) => `₹ ${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

interface Snapshot {
  products: Product[];
  employees: Employee[];
  salesOrders: SalesOrder[];
  invoices: Invoice[];
  stock: Stock[];
  categories: Category[];
  stores: Store[];
  warehouses: Warehouse[];
}

const emptySnap: Snapshot = {
  products: [], employees: [], salesOrders: [], invoices: [], stock: [], categories: [], stores: [], warehouses: [],
};

async function safeAll<T>(fn: () => Promise<T[]>): Promise<T[]> {
  try { const r = await fn(); return Array.isArray(r) ? r : []; } catch { return []; }
}

export default function Home() {
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;
  const navigate = useNavigate();
  const { user } = useAuth();
  const canSeeFinance = useHasRole(['admin', 'manager']);

  const [snap, setSnap] = useState<Snapshot>(emptySnap);
  const [loading, setLoading] = useState(false);
  const [anyFailed, setAnyFailed] = useState(false);
  const colors = useThemeChartColors();

  const load = useCallback(async () => {
    setLoading(true); setAnyFailed(false);
    try {
      const [products, employees, salesOrders, invoices, stock, categories, stores, warehouses] = await Promise.all([
        safeAll(() => productService.all()),
        safeAll(() => employeeService.all()),
        safeAll(() => salesOrderService.all()),
        safeAll(() => invoiceService.all()),
        safeAll(() => stockService.all()),
        safeAll(() => categoryService.all()),
        safeAll(() => storeService.all()),
        safeAll(() => warehouseService.all()),
      ]);
      setSnap({ products, employees, salesOrders, invoices, stock, categories, stores, warehouses });
    } catch (e) {
      setAnyFailed(true);
      toastRef.current.error('Load failed', e instanceof Error ? e.message : 'Failed');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const kpis = useMemo(() => {
    const activeProducts = snap.products.filter((p) => p.isActive).length;
    // Employee.status is the live canonical field (int enum: 1=Active,
    // 2=OnLeave, 3=Terminated, 4=Resigned — see EmployeeStatusLabels).
    // The legacy `isActive` bool is still declared on the type for
    // backward compat but the live API does not populate it, so the
    // previous `e.isActive` filter counted 0 even when every row was
    // marked Active in the panel. Prefer `status === 1`; fall back to
    // `isActive` only when the row genuinely has no `status` (e.g.
    // legacy fixtures / mocks).
    const activeEmployees = snap.employees.filter((e) => {
      if (e.status != null) return Number(e.status) === 1;
      return e.isActive === true;
    }).length;
    const lowStock = snap.stock.filter((s) => Number(s.reorderLevel ?? 0) > 0 && Number(s.quantity) <= Number(s.reorderLevel)).length;
    const outstandingInvoices = snap.invoices.filter((i) => String(i.status ?? '').toLowerCase() !== 'paid' && String(i.status ?? '').toLowerCase() !== 'cancelled');
    const totalOutstanding = outstandingInvoices.reduce((sum, i) => sum + Number(i.balanceAmount ?? i.totalAmount ?? 0), 0);
    const totalRevenue = snap.salesOrders.reduce((sum, o) => sum + Number(o.totalAmount ?? 0), 0);
    return [
      { id: 'products', title: 'Products', value: `${activeProducts} / ${snap.products.length}`, subtitle: 'Active / Total', icon: <Boxes className="h-5 w-5 text-primary" />, onClick: () => navigate('/panel/products') },
      { id: 'employees', title: 'Employees', value: `${activeEmployees} / ${snap.employees.length}`, subtitle: 'Active / Total', icon: <Users className="h-5 w-5 text-info" />, onClick: () => navigate('/panel/employees') },
      { id: 'orders', title: 'Sales orders', value: snap.salesOrders.length, icon: <ShoppingCart className="h-5 w-5 text-success" />, onClick: () => navigate('/panel/sales-orders') },
      { id: 'lowStock', title: 'Low stock', value: lowStock, subtitle: 'At or below reorder', icon: <AlertTriangle className="h-5 w-5 text-warning" />, onClick: () => navigate('/panel/stock') },
      ...(canSeeFinance ? [
        { id: 'revenue', title: 'Revenue', value: inr(totalRevenue), subtitle: 'All-time orders', icon: <TrendingUp className="h-5 w-5 text-success" />, onClick: () => navigate('/panel/sales-orders/dashboard') },
        { id: 'outstanding', title: 'Outstanding', value: inr(totalOutstanding), subtitle: `${outstandingInvoices.length} unpaid invoice(s)`, icon: <IndianRupee className="h-5 w-5 text-danger" />, onClick: () => navigate('/panel/invoices') },
      ] : []),
      { id: 'categories', title: 'Categories', value: snap.categories.length, icon: <Layers className="h-5 w-5 text-info" />, onClick: () => navigate('/panel/categories') },
      { id: 'locations', title: 'Locations', value: `${snap.stores.length} + ${snap.warehouses.length}`, subtitle: 'Stores + Warehouses', icon: <WarehouseIcon className="h-5 w-5 text-primary" />, onClick: () => navigate('/panel/stores') },
    ];
  }, [snap, canSeeFinance, navigate]);

  const ordersByChannel = useMemo(() => {
    const m = new Map<string, number>();
    for (const o of snap.salesOrders) {
      const k = String(o.channel ?? 'Unassigned');
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return [...m.entries()].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [snap.salesOrders]);

  const productsByCategory = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of snap.products) {
      const k = p.categoryId || 'Unassigned';
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return [...m.entries()]
      .map(([k, v]) => ({ name: k.length > 12 ? `${k.slice(0, 6)}…${k.slice(-4)}` : k, fullKey: k, count: v }))
      .sort((a, b) => b.count - a.count).slice(0, 8);
  }, [snap.products]);

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface-card px-4 py-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">
            Welcome{user?.name ? `, ${user.name}` : ''}.
          </h1>
          <p className="text-xs text-content-muted">Cross-tenant snapshot — click any card to drill in.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {anyFailed && (
        <Alert variant="warning">
          Some entities failed to load. KPIs below reflect the modules that responded successfully.
        </Alert>
      )}

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <KpiCard
            key={k.id}
            title={k.title}
            subtitle={k.subtitle}
            value={k.value}
            icon={k.icon}
            onClick={k.onClick}
          />
        ))}
      </div>

      <RecentNotificationsCard />

      <div className="grid gap-3 lg:grid-cols-2">
        <Card>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-content-primary">Orders by channel</h3>
            <span className="text-[11px] text-content-muted">{snap.salesOrders.length} total</span>
          </div>
          {ordersByChannel.length === 0 ? (
            <div className="flex h-[260px] items-center justify-center text-xs text-content-muted">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={ordersByChannel} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {ordersByChannel.map((d, i) => <Cell key={d.name} fill={colors[i % colors.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => <span style={{ color: 'var(--color-text-secondary)' }}>{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-content-primary">Top categories by product count</h3>
            <span className="text-[11px] text-content-muted">{snap.products.length} products</span>
          </div>
          {productsByCategory.length === 0 ? (
            <div className="flex h-[260px] items-center justify-center text-xs text-content-muted">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={productsByCategory} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="name" tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} cursor={{ fill: 'var(--color-bg-card-hover)' }} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={40}>
                  {productsByCategory.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
    </div>
  );
}
